import { useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useSystemStatus } from '../../hooks/useSystemStatus'

function StatusDot({ status }) {
  if (status === true || status === 'ok' || status === 'up' || status === 'ready') {
    return <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" />
  }
  if (status === false || status === 'degraded' || status === 'down' || status === 'error') {
    return <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
  }
  return <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500" />
}

function formatTime(isoString) {
  if (!isoString) return '—'
  const date = new Date(isoString)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function StatusDashboard({ onClose, isDarkMode }) {
  const { data, loading, refresh } = useSystemStatus()
  const [_refreshNotification, setRefreshNotification] = useState(0)

  const supabase = data?.services?.supabase || {}
  const uptimerobot = data?.services?.uptimerobot || {}
  const netlify = data?.services?.netlify || {}

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-lg shadow-2xl ${
          isDarkMode ? 'bg-slate-800' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between border-b px-6 py-4 ${
            isDarkMode ? 'border-slate-700' : 'border-gray-200'
          }`}
        >
          <div>
            <h2
              className={`text-lg font-bold ${
                isDarkMode ? 'text-slate-100' : 'text-gray-900'
              }`}
            >
              System Status
            </h2>
            <p
              className={`text-xs mt-1 ${
                isDarkMode ? 'text-slate-400' : 'text-gray-500'
              }`}
            >
              Last checked: {formatTime(data?.timestamp)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              disabled={loading}
              className={`px-3 py-1 text-sm rounded font-medium transition ${
                isDarkMode
                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-100'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              onClick={onClose}
              className={`p-1 rounded transition ${
                isDarkMode
                  ? 'hover:bg-slate-700 text-slate-400'
                  : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Push notification permission prompt */}
          {'Notification' in window && Notification.permission === 'default' && (
            <div className={`flex items-center justify-between p-3 rounded-lg border ${
              isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-amber-50 border-amber-200'
            }`}>
              <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-amber-800'}`}>
                Enable push notifications to get alerted when the app is in the background
              </span>
              <button
                onClick={async () => {
                  await Notification.requestPermission()
                  setRefreshNotification(prev => prev + 1)  // trigger re-render
                }}
                className="ml-3 px-3 py-1 bg-sfp-orange text-white text-sm rounded font-medium flex-shrink-0"
              >
                Enable
              </button>
            </div>
          )}
          {/* Supabase Card */}
          <div
            className={`rounded-lg border p-4 ${
              isDarkMode
                ? 'bg-slate-700/50 border-slate-600'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                <StatusDot status={supabase.ok} />
                <div className="flex-1 min-w-0">
                  <h3
                    className={`font-semibold ${
                      isDarkMode ? 'text-slate-100' : 'text-gray-900'
                    }`}
                  >
                    Supabase Database
                  </h3>
                  <p
                    className={`text-sm break-words ${
                      supabase.ok === false && supabase.error ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-slate-400' : 'text-gray-600')
                    }`}
                  >
                    {supabase.ok === true ? '✓ Connected' : supabase.error ? `✗ ${supabase.error}` : '? Checking…'}
                  </p>
                </div>
              </div>
              {supabase.latency_ms !== undefined && (
                <span
                  className={`text-sm font-mono ${
                    isDarkMode ? 'text-slate-400' : 'text-gray-600'
                  }`}
                >
                  {supabase.latency_ms}ms
                </span>
              )}
            </div>
          </div>

          {/* UptimeRobot Card */}
          <div
            className={`rounded-lg border p-4 ${
              isDarkMode
                ? 'bg-slate-700/50 border-slate-600'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between mb-3 gap-4">
              <div className="flex items-center gap-3 flex-1">
                <StatusDot status={uptimerobot.ok} />
                <div className="flex-1 min-w-0">
                  <h3
                    className={`font-semibold ${
                      isDarkMode ? 'text-slate-100' : 'text-gray-900'
                    }`}
                  >
                    Uptime Monitors
                  </h3>
                  <p
                    className={`text-sm break-words ${
                      uptimerobot.error ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-slate-400' : 'text-gray-600')
                    }`}
                  >
                    {uptimerobot.error ? `✗ ${uptimerobot.error}` : `${uptimerobot.monitors?.length || 0} monitors`}
                  </p>
                </div>
              </div>
            </div>
            {uptimerobot.monitors?.length > 0 && (
              <div className="space-y-2">
                {uptimerobot.monitors.map((m) => (
                  <div
                    key={m.id}
                    className={`flex items-center justify-between text-sm p-2 rounded ${
                      isDarkMode ? 'bg-slate-600/50' : 'bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <StatusDot status={m.status} />
                      <span
                        className={`font-mono truncate ${
                          isDarkMode ? 'text-slate-300' : 'text-gray-700'
                        }`}
                        title={m.url}
                      >
                        {m.name}
                      </span>
                    </div>
                    <span
                      className={`text-xs font-mono flex-shrink-0 ml-2 ${
                        isDarkMode ? 'text-slate-400' : 'text-gray-500'
                      }`}
                    >
                      {(m.uptime_ratio || 0).toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Netlify Card */}
          <div
            className={`rounded-lg border p-4 ${
              isDarkMode
                ? 'bg-slate-700/50 border-slate-600'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between mb-2 gap-4">
              <div className="flex items-center gap-3 flex-1">
                <StatusDot status={netlify.ok} />
                <div className="flex-1 min-w-0">
                  <h3
                    className={`font-semibold ${
                      isDarkMode ? 'text-slate-100' : 'text-gray-900'
                    }`}
                  >
                    Netlify Hosting
                  </h3>
                  <p
                    className={`text-sm break-words ${
                      netlify.ok === false && netlify.error ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-slate-400' : 'text-gray-600')
                    }`}
                  >
                    {netlify.ok === true
                      ? `✓ ${netlify.last_deploy_state}`
                      : netlify.error
                        ? `✗ ${netlify.error}`
                        : '? Checking…'}
                  </p>
                </div>
              </div>
            </div>
            {netlify.branch && (
              <div
                className={`text-sm p-2 rounded space-y-1 ${
                  isDarkMode ? 'bg-slate-600/50' : 'bg-white'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`${
                      isDarkMode ? 'text-slate-400' : 'text-gray-600'
                    }`}
                  >
                    Branch:
                  </span>
                  <span
                    className={`font-mono font-semibold ${
                      isDarkMode ? 'text-slate-200' : 'text-gray-900'
                    }`}
                  >
                    {netlify.branch}
                  </span>
                </div>
                {netlify.commit_ref && (
                  <div className="flex items-center gap-2">
                    <span
                      className={`${
                        isDarkMode ? 'text-slate-400' : 'text-gray-600'
                      }`}
                    >
                      Commit:
                    </span>
                    <span
                      className={`font-mono font-semibold ${
                        isDarkMode ? 'text-slate-200' : 'text-gray-900'
                      }`}
                    >
                      {netlify.commit_ref}
                    </span>
                  </div>
                )}
                {netlify.commit_message && (
                  <div className="flex items-start gap-2">
                    <span
                      className={`flex-shrink-0 ${
                        isDarkMode ? 'text-slate-400' : 'text-gray-600'
                      }`}
                    >
                      Message:
                    </span>
                    <span
                      className={`${
                        isDarkMode ? 'text-slate-200' : 'text-gray-900'
                      }`}
                    >
                      {netlify.commit_message}
                    </span>
                  </div>
                )}
                {netlify.last_deploy_at && (
                  <div className="flex items-center gap-2 text-xs pt-1 border-t border-slate-500/30">
                    <span
                      className={`${
                        isDarkMode ? 'text-slate-500' : 'text-gray-500'
                      }`}
                    >
                      Deployed:
                    </span>
                    <span
                      className={`font-mono ${
                        isDarkMode ? 'text-slate-400' : 'text-gray-600'
                      }`}
                    >
                      {formatTime(netlify.last_deploy_at)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
